import { InjectionToken } from '@angular/core';
import {
  type IAccountCredit,
  type IAccountCreditExtendedData,
  type IAppointment,
  type IAppointmentCreditClassification,
  type IInvoice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';

export interface IAppointmentCreditState
  extends IAppointmentCreditClassification {
  matchingTotal: number;
  /**
   * Remaining balance of the credits held for no particular appointment. Lives
   * beside `matchingTotal` so both figures a checkout shows come from the same
   * live credit read, instead of one of them drifting off a denormalised
   * patient summary.
   */
  unreservedTotal: number;
  /**
   * Everything the patient still has, `matching` + `unreserved` +
   * `otherAppointment`. This is the figure a checkout leads with, because it is
   * the same figure the Account Credit payment option reports; leading with the
   * two pre-selectable buckets hid credit reserved for another appointment from
   * one place while the other went on offering it.
   */
  availableTotal: number;
}

export interface IAccountCreditTransactionProvider {
  getAppointmentCreditState$(
    invoice: WithRef<IInvoice>,
    appointmentRef?: DocumentReference<IAppointment>
  ): Observable<IAppointmentCreditState>;

  autoApplyAppointmentCredits(
    invoice: WithRef<IInvoice>,
    appointmentRef: DocumentReference<IAppointment>
  ): Promise<
    DocumentReference<ITransaction<IAccountCreditExtendedData>> | undefined
  >;

  refundTransaction(
    invoice: WithRef<IInvoice>,
    transaction: WithRef<ITransaction<IAccountCreditExtendedData>>,
    fromCredit?: WithRef<IAccountCredit>
  ): Promise<
    DocumentReference<ITransaction<IAccountCreditExtendedData>> | undefined
  >;
}

export const ACCOUNT_CREDIT_TRANSACTION_PROVIDER =
  new InjectionToken<IAccountCreditTransactionProvider>(
    'ACCOUNT_CREDIT_TRANSACTION_PROVIDER'
  );
